import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  IActivo,
  IConfigReenvio,
  IFilter,
  IPopulate,
  IQueryParam,
  MetodoReenvio,
} from 'modelos/src';
import {
  Subscription,
  distinctUntilChanged,
  firstValueFrom,
  from,
  switchMap,
  timer,
} from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../../../auxiliares/tabla/filtro/filtro.component';
import {
  IColumnas,
  ICrearBoton,
} from '../../../../../auxiliares/tabla/tabla.component';
import { ConfigReenviosService } from '../../../../../auxiliares/servicios/http/config-reenvios.service';
import { ActivosService } from '../../../../../auxiliares/servicios/http/activos.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { LoginService } from '../../../../login/login.service';
import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';
import {
  ITestSoflexData,
  TestSoflexComponent,
} from '../test-soflex/test-soflex.component';
import { configReenvioVigente } from '../../trackers/reenvios-tracker-dialog/reenvios-tracker-dialog.component';
import { ClienteJerarquiaService } from '../../../../../auxiliares/servicios/cliente-jerarquia.service';
import {
  EmergenciaAvlDialogComponent,
  IEmergenciaAvlData,
} from '../emergencia-avl-dialog/emergencia-avl-dialog.component';
import {
  EnviosEmergenciaAvlDialogComponent,
  IEnviosEmergenciaAvlData,
} from '../envios-emergencia-avl-dialog/envios-emergencia-avl-dialog.component';
import { escaparHtml } from '../../../../../auxiliares/html-seguro.util';
import { estadoHtml, fechaCelda } from '../../../../../auxiliares/tabla/celdas';
import { IEstadoCelda } from '../../../../../auxiliares/tabla/tabla.interfaces';

/** Valores del filtro "Vigencia". No es un campo de la base: `listar` lo
 *  reemplaza por las condiciones de período con el "ahora" de cada consulta. */
type Vigencia = 'vigentes' | 'vencidas' | 'programadas';
const CAMPO_VIGENCIA = 'vigencia';

@Component({
  selector: 'app-listado-config-reenvios',
  templateUrl: './listado-config-reenvios.component.html',
  standalone: false,
})
export class ListadoConfigReenviosComponent implements OnInit, OnDestroy {
  private clienteJerarquia = inject(ClienteJerarquiaService);
  private lista2 = inject(Lista2Service);
  helper = inject(HelperService);
  private dialogService = inject(DialogService);
  private formularios = inject(FormularioDialogService);
  private service = inject(ConfigReenviosService);
  private activosService = inject(ActivosService);
  private navigator = inject(Router);
  private route = inject(ActivatedRoute);

  //// Tabla nueva
  public name = 'ListadoConfigReenviosComponent';
  public datos?: IConfigReenvio[];
  public totalCount = 0;
  /// Vehículos de las configs por vehículo: no hay populate para ese caso
  /// (`idEntidadReenvio` solo popula tracker o alarma), así que se piden
  /// aparte. `idTracker` es el que tiene hoy, el que usa el reenvío.
  private vehiculos = new Map<string, IActivo>();
  public columnas?: IColumnas<IConfigReenvio>[];

  //Subs
  public datos$?: Subscription;

  public botonCrear: ICrearBoton = {
    mostrar: this.helper.tienePermiso(
      'Administración - Crear configuraciones de reenvíos',
    ),
    tooltip: 'Crear configuración de reenvío',
    color: 'primary',
    tipo: 'material',
    icon: 'add',
    accion: () => this.crear(),
  };
  public search: IRegExpSearch = {
    fields: ['opcionesReenvio.host'],
  };

  // FILTROS
  // Cliente que creó la configuración
  public cliente: IFiltroTabla = {
    filter: {
      field: 'idCliente',
    },
    label: 'Cliente creador',
    tipo: 'cliente-selector',
  };
  // Cliente cuyos trackers o alarmas se reenvían (agrupaciones "Todos los
  // trackers/alarmas del cliente"; la columna Entidad lo muestra como Cliente)
  public clienteReenvio: IFiltroTabla = {
    filter: {
      field: 'idClienteReenvio',
    },
    label: 'Cliente reenviado',
    tipo: 'cliente-selector',
  };
  // Cliente en cuya jerarquía cuelga la entidad: el cliente elegido y toda su
  // descendencia.
  public ancestro: IFiltroTabla = {
    filter: {
      field: 'idsAncestros',
      incluirPropio: true,
    },
    label: 'En la jerarquía de',
    tipo: 'cliente-selector',
  };
  public filtroAlarma: IFiltroTabla = {
    elementos: [],
    filter: {
      field: 'idEntidadReenvio',
    },
    label: 'Alarma',
    selectLabel: 'nombre',
    selectValue: '_id',
    tipo: 'ngselect-async',
    asyncFunction: 'dispositivosAlarmas',
    searchOn: ['nombre'],
  };
  public filtroTracker: IFiltroTabla = {
    elementos: [],
    filter: {
      field: 'idEntidadReenvio',
    },
    label: 'Tracker',
    selectLabel: 'nombre',
    selectValue: '_id',
    tipo: 'ngselect-async',
    asyncFunction: 'trackers',
    searchOn: ['nombre'],
  };
  /// Configs guardadas sobre el vehículo (las que siguen al tracker que tenga
  /// asignado), no las de su tracker: para esas está el filtro Tracker.
  public filtroVehiculo: IFiltroTabla = {
    elementos: [],
    filter: {
      field: 'idEntidadReenvio',
    },
    label: 'Vehículo',
    selectLabel: 'identificacion',
    selectValue: '_id',
    tipo: 'ngselect-async',
    asyncFunction: 'vehiculos',
    searchOn: ['identificacion', 'vehiculo.patente'],
  };
  public filtroAgrupacion: IFiltroTabla = {
    elementos: [
      'Todos los trackers del cliente',
      'Todas las alarmas del cliente',
      'Entidad',
    ],
    filter: {
      field: 'agrupacionReenvio',
    },
    label: 'Agrupación',
    tipo: 'ngselect',
  };
  public filtroMetodo: IFiltroTabla = {
    elementos: [
      'Básico',
      'Seguridad Evento Externo',
      'Caessat',
      'Cersat',
      'Iron Tracking',
      'Logictracker',
    ] as MetodoReenvio[],
    filter: {
      field: 'opcionesReenvio.metodo',
    },
    label: 'Tipo de reenvío',
    tipo: 'ngselect',
  };
  // public filtroPuerto: IFiltroTabla = { no funciona por que es numero dentro un objeto
  //   filter: {
  //     field: 'opcionesReenvio.puerto',
  //   },
  //   label: 'Puerto',
  //   tipo: 'input',
  // };
  public filtroActivo: IFiltroTabla = {
    elementos: [
      { label: 'Activas', value: true },
      { label: 'Desactivadas', value: false },
    ],
    filter: {
      field: 'activo',
    },
    label: 'Estado',
    rapido: true,
    selectLabel: 'label',
    selectValue: 'value',
    tipo: 'select',
  };
  /** Si el período incluye el momento actual. Con "Objetivo AVL" en Tipo es
   *  la forma de ver las emergencias que están transmitiendo ahora. */
  public filtroVigencia: IFiltroTabla = {
    elementos: [
      { label: 'Vigentes ahora', value: 'vigentes' },
      { label: 'Vencidas', value: 'vencidas' },
      { label: 'Programadas (no empezaron)', value: 'programadas' },
    ],
    filter: {
      field: CAMPO_VIGENCIA,
    },
    label: 'Vigencia',
    rapido: true,
    selectLabel: 'label',
    selectValue: 'value',
    tipo: 'ngselect',
  };
  public filtros: IFiltroTabla[] = [
    this.filtroAlarma,
    this.filtroTracker,
    this.filtroVehiculo,
    this.filtroAgrupacion,
    this.filtroMetodo,
    this.filtroActivo,
    this.filtroVigencia,
  ];

  //Query
  public filter: IFilter<IConfigReenvio> = {};
  public populate: IPopulate = [
    { path: 'cliente', select: 'nombre' },
    { path: 'clienteReenvio', select: 'nombre' },
    { path: 'tracker', select: 'nombre' },
    { path: 'dispositivoAlarma', select: 'nombre' },
  ];
  public query: IQueryParam = {
    filter: JSON.stringify(this.filter),
    populate: JSON.stringify(this.populate),
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    includeChildren: true,
    childrenLevel: 0,
    sort: '-fechaCreacion',
  };

  //Listados

  /// Poll de 30 s como el resto de los listados, pero con la query rearmada
  /// en cada vuelta: el filtro "Vigencia" depende de la hora y con la query
  /// fija una emergencia que vence seguiría figurando como vigente.
  public async listar(): Promise<void> {
    this.datos$?.unsubscribe();
    this.datos$ = timer(0, 30000)
      .pipe(
        switchMap(() => from(this.service.getFiltered(this.queryEfectiva()))),
        distinctUntilChanged(
          (prev, curr) =>
            JSON.stringify(prev.datos) === JSON.stringify(curr.datos) &&
            prev.totalCount === curr.totalCount,
        ),
      )
      .subscribe(async (data) => {
        await this.resolverVehiculos(data.datos);
        this.totalCount = data.totalCount;
        this.datos = data.datos;
      });
  }

  private async resolverVehiculos(datos: IConfigReenvio[]): Promise<void> {
    const ids = [
      ...new Set(
        datos
          .filter(
            (d) =>
              d.agrupacionReenvio === 'Entidad' &&
              d.idEntidadReenvio &&
              !d.tracker &&
              !d.dispositivoAlarma,
          )
          .map((d) => d.idEntidadReenvio!),
      ),
    ];
    if (!ids.length) {
      this.vehiculos = new Map();
      return;
    }
    try {
      const listado = await this.activosService.getFiltered({
        filter: JSON.stringify({ _id: { $in: ids } }),
        select: 'identificacion idTracker',
        includeChildren: true,
        childrenLevel: 0,
        unlimited: true,
      });
      this.vehiculos = new Map(listado.datos.map((v) => [v._id!, v]));
    } catch (error) {
      console.error('Error al resolver los vehículos de las configs', error);
    }
  }

  private vehiculo(dato: IConfigReenvio): IActivo | undefined {
    return dato.idEntidadReenvio
      ? this.vehiculos.get(dato.idEntidadReenvio)
      : undefined;
  }

  /** Tracker al que se aplica una config por entidad: el propio o, si es por
   *  vehículo, el que el vehículo tenga asignado hoy. */
  private idTrackerDe(dato: IConfigReenvio): string | undefined {
    const vehiculo = this.vehiculo(dato);
    if (vehiculo) return vehiculo.idTracker || undefined;
    return dato.idEntidadReenvio;
  }

  /** La query tal cual la armó la tabla, con el pseudo-campo `vigencia`
   *  reemplazado por condiciones sobre `periodoInicio` / `periodoFin`. `null`
   *  matchea también las configs sin el campo (sin período = siempre). */
  private queryEfectiva(): IQueryParam {
    const filter = HelperService.stringToObject(this.query.filter) as
      | { $and?: Record<string, unknown>[] }
      | undefined;
    const and = filter?.$and;
    const i = and?.findIndex((f) => CAMPO_VIGENCIA in f) ?? -1;
    if (!and || i < 0) return this.query;

    const vigencia = and[i][CAMPO_VIGENCIA] as Vigencia;
    const ahora = new Date().toISOString();
    const condiciones: Record<Vigencia, Record<string, unknown>[]> = {
      vigentes: [
        { activo: { $ne: false } },
        {
          $or: [{ periodoInicio: null }, { periodoInicio: { $lte: ahora } }],
        },
        { $or: [{ periodoFin: null }, { periodoFin: { $gte: ahora } }] },
      ],
      vencidas: [{ periodoFin: { $lt: ahora } }],
      programadas: [{ periodoInicio: { $gt: ahora } }],
    };
    const nuevoAnd = [
      ...and.slice(0, i),
      ...(condiciones[vigencia] ?? []),
      ...and.slice(i + 1),
    ];
    return {
      ...this.query,
      filter: JSON.stringify({ ...filter, $and: nuevoAnd }),
    };
  }

  // Acciones
  public async crear() {
    this.navigator.navigate(['crear'], { relativeTo: this.route });
  }

  public async editar(configreenvio: IConfigReenvio) {
    this.navigator.navigate(['editar', configreenvio._id], {
      relativeTo: this.route,
    });
  }

  /// Test de la config Soflex: no envía nada a Soflex, muestra qué vehículos
  /// se reenviarían y qué datos faltan para crearlos en Soflex.
  public probarConfigSoflex(dato: IConfigReenvio) {
    this.formularios.abrir<TestSoflexComponent, ITestSoflexData>(
      TestSoflexComponent,
      {
        tamano: 'grande',
        data: { idConfigReenvio: dato._id },
        cerrarAfuera: true,
      },
    );
  }

  /** Las acciones AVL van solo en las emergencias por vehículo: una config
   *  "todos los trackers del cliente" con Objetivo AVL afecta a muchos, y el
   *  popup es para uno. */
  private esEmergenciaAvl(dato: IConfigReenvio): boolean {
    return (
      dato.opcionesReenvio?.metodo === 'Objetivo AVL' &&
      dato.agrupacionReenvio === 'Entidad' &&
      !!this.idTrackerDe(dato) &&
      !!dato._id
    );
  }

  public ajustarEmergenciaAvl(dato: IConfigReenvio): void {
    if (!this.esEmergenciaAvl(dato)) return;
    this.formularios
      .abrir<EmergenciaAvlDialogComponent, IEmergenciaAvlData>(
        EmergenciaAvlDialogComponent,
        {
          tamano: 'mediano',
          data: {
            idTracker: this.idTrackerDe(dato)!,
            config: dato,
            nombreEntidad:
              dato.tracker?.nombre ?? this.vehiculo(dato)?.identificacion,
          },
        },
      )
      .afterClosed()
      .subscribe((cambio) => {
        if (cambio) this.listar();
      });
  }

  public verEnviosAvl(dato: IConfigReenvio): void {
    if (!this.esEmergenciaAvl(dato)) return;
    this.formularios.abrir<
      EnviosEmergenciaAvlDialogComponent,
      IEnviosEmergenciaAvlData
    >(EnviosEmergenciaAvlDialogComponent, {
      tamano: 'mediano',
      cerrarAfuera: true,
      data: {
        config: dato,
        idTracker: this.idTrackerDe(dato),
        nombreEntidad:
          dato.opcionesReenvio?.opcionesObjetivoAVL?.deviceid ??
          dato.tracker?.nombre ??
          this.vehiculo(dato)?.identificacion,
      },
    });
  }

  public async verReglasReenvio(dato: IConfigReenvio) {
    const reglas = dato.opcionesReenvio?.reglasReenvio;
    if (!reglas?.length) {
      this.dialogService.dialog(
        'Reglas de reenvío',
        'No hay reglas de reenvío configuradas.',
      );
      return;
    }

    const sinDato = '<span class="no-info">Sin información</span>';
    const filas = reglas
      .map(
        (r) =>
          `<tr>
            <td>${r.puertoEntrada != null ? escaparHtml(r.puertoEntrada) : sinDato}</td>
            <td>${r.protocoloEntrada != null ? escaparHtml(r.protocoloEntrada) : sinDato}</td>
            <td>${r.hostSalida != null ? escaparHtml(r.hostSalida) : sinDato}</td>
            <td>${r.puertoSalida != null ? escaparHtml(r.puertoSalida) : sinDato}</td>
            <td>${r.protocoloSalida != null ? escaparHtml(r.protocoloSalida) : sinDato}</td>
          </tr>`,
      )
      .join('');

    const mensaje = `
      <table border="1" cellpadding="8" cellspacing="0" width="100%" align="center">
        <thead>
          <tr>
            <th>Puerto Entrada</th>
            <th>Protocolo</th>
            <th>Host Salida</th>
            <th>Puerto Salida</th>
            <th>Protocolo</th>
          </tr>
        </thead>
        <tbody align="center">${filas}</tbody>
      </table>`;

    this.dialogService.dialog('Reglas de reenvío', mensaje, 'Cerrar');
  }

  /// Activa o desactiva la config sin borrarla: con `activo: false` los
  /// servicios de reenvío la ignoran (trackers-go la descarta al leerla de
  /// Redis) pero queda guardada para volver a prenderla.
  public async cambiarActivo(configreenvio: IConfigReenvio, activo: boolean) {
    const accion = activo ? 'activar' : 'desactivar';
    const res = await this.dialogService.confirm(
      `${activo ? 'Activar' : 'Desactivar'} configuración de reenvío`,
      activo
        ? '¿Querés activar la configuración? Vuelve a reenviarse la data.'
        : '¿Querés desactivar la configuración? Deja de reenviarse la data, pero la configuración se conserva.',
    );
    if (!res) return;
    try {
      await this.service.update(configreenvio._id!, { activo });
      this.helper.notifSuccess(
        `Configuración de reenvío ${activo ? 'activada' : 'desactivada'}`,
      );
      await this.listar();
    } catch (error) {
      console.error(`Error al ${accion} la configuración de reenvío`, error);
      this.helper.notifError(error);
    }
  }

  public async eliminar(configreenvio: IConfigReenvio) {
    const res = await this.dialogService.confirm(
      'Eliminar configuración de reenvío',
      `¿Querés eliminar la configuración de reenvío?`,
    );
    if (!res) return;
    try {
      await this.service.delete(configreenvio._id!);
      this.helper.notifSuccess('Configuración de reenvío eliminada');
      await this.listar();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  // Tabla

  /** "16:04", o "24/09/2026 16:04" si no es hoy: una emergencia larga cruza el día. */
  private horaCorta(fecha: string): string {
    const completa = fechaCelda(fecha);
    const esHoy = new Date(fecha).toDateString() === new Date().toDateString();
    return esHoy ? completa.slice(11) : completa;
  }

  /** Ícono del método: sólo los que tienen concepto en docs/ICONOS.md
   *  (Objetivo AVL, Integraciones); el resto es un reenvío. */
  private getIconoMetodo(metodo?: string): string {
    switch (metodo) {
      case 'Objetivo AVL':
        return 'emergency_share';
      case 'Soflex':
        return 'cable';
      default:
        return 'forward_to_inbox';
    }
  }

  private getIconoAgrupacion(agrupacion?: string): string | undefined {
    switch (agrupacion) {
      case 'Todos los trackers del cliente':
        return 'gps_fixed';
      case 'Todas las alarmas del cliente':
        return 'doorbell';
      default:
        return undefined;
    }
  }

  /** Dato con su ícono de entidad (clase común de la tabla). */
  private linea(texto: string, icono?: string, clase = ''): string {
    const i = icono
      ? `<span class="material-symbols-outlined">${icono}</span>`
      : '';
    return `<div class="tabla-linea ${clase}">${i}<span>${texto}</span></div>`;
  }

  private estadoConfig(dato: IConfigReenvio): IEstadoCelda {
    if (dato.activo === false) {
      return {
        texto: 'Desactivada',
        tono: 'neutro',
        icono: 'block',
        tooltip: 'La configuración está guardada pero no reenvía nada.',
      };
    }
    if (configReenvioVigente(dato)) {
      return { texto: 'Activa', tono: 'ok', icono: 'check_circle' };
    }
    return { texto: 'Fuera de período', tono: 'aviso', icono: 'date_range' };
  }

  private setColumnas() {
    const columnas: IColumnas<IConfigReenvio>[] = [
      // Estado: activa / desactivada (y si el período la deja fuera de vigencia)
      {
        header: {
          label: 'Estado',
        },
        row: {
          estado: (dato) => this.estadoConfig(dato),
        },
      },

      // Fecha de creación (los segundos, en el tooltip)
      {
        header: {
          label: 'Fecha',
          sortable: true,
          sort: 'fechaCreacion',
        },
        row: {
          html: (dato) => {
            const fecha = fechaCelda(dato.fechaCreacion);
            return fecha ? `<span class="tabla-fecha">${fecha}</span>` : '';
          },
          tooltip: (dato) =>
            dato.fechaCreacion
              ? `${fechaCelda(dato.fechaCreacion)}:${String(new Date(dato.fechaCreacion).getSeconds()).padStart(2, '0')}`
              : undefined,
          noData: 'Sin fecha',
        },
      },

      // Agrupación
      {
        header: {
          label: 'Agrupación',
        },
        row: {
          html: (dato) => {
            const agrupacion = dato.agrupacionReenvio;
            if (!agrupacion) return '';
            return this.linea(
              escaparHtml(agrupacion),
              this.getIconoAgrupacion(agrupacion),
            );
          },
          noData: 'Sin agrupación',
        },
      },

      // Selección (Entidad)
      {
        header: {
          label: 'Entidad',
        },
        row: {
          html: (dato) => {
            const seleccion =
              dato.tracker?.nombre ||
              dato.dispositivoAlarma?.nombre ||
              this.vehiculo(dato)?.identificacion ||
              dato.clienteReenvio?.nombre ||
              '';
            if (!seleccion) return '';

            let tipoEntidad = '';
            let iconoEntidad: string | undefined;
            if (dato.tracker?.nombre) {
              tipoEntidad = 'Tracker';
              iconoEntidad = 'gps_fixed';
            } else if (dato.dispositivoAlarma?.nombre) {
              tipoEntidad = 'Alarma';
              iconoEntidad = 'doorbell';
            } else if (this.vehiculo(dato)?.identificacion) {
              tipoEntidad = 'Vehículo';
              iconoEntidad = 'directions_car';
            } else if (dato.clienteReenvio?.nombre) {
              tipoEntidad = 'Cliente';
              iconoEntidad = 'business';
            }

            return `<div class="tabla-celda-col">
              ${this.linea(escaparHtml(seleccion), iconoEntidad, 'tabla-principal')}
              <span class="tabla-secundario">${tipoEntidad}</span>
            </div>`;
          },
          tooltip: (dato) => {
            const seleccion =
              dato.tracker?.nombre ||
              dato.dispositivoAlarma?.nombre ||
              this.vehiculo(dato)?.identificacion ||
              dato.clienteReenvio?.nombre;
            return seleccion
              ? `<div><strong>Entidad:</strong> ${escaparHtml(seleccion)}</div>`
              : undefined;
          },
          noData: 'Sin entidad',
        },
      },

      // Reenviar Hijos: es un dato, Sí / No como texto
      {
        header: {
          label: 'Reenviar a clientes hijos',
        },
        row: {
          parse: (dato) => (dato.reenviarHijos ? 'Sí' : 'No'),
        },
      },

      // Opciones de reenvío
      {
        header: {
          label: 'Opciones de reenvío',
        },
        row: {
          html: (dato) => {
            const opts = dato.opcionesReenvio;
            if (!opts?.metodo) return '';

            let html = `<div class="tabla-celda-col">`;

            // Método
            html += this.linea(
              escaparHtml(opts.metodo),
              this.getIconoMetodo(opts.metodo),
              'tabla-principal',
            );

            // Host:Puerto
            if (opts.host && opts.puerto) {
              html += `<span class="tabla-secundario tabla-codigo">${escaparHtml(opts.host)}:${escaparHtml(opts.puerto)}</span>`;
            }

            // Protocolo
            const esLogictracker = opts.metodo === 'Logictracker';
            const protocolo = esLogictracker ? 'http' : opts.protocolo;
            if (protocolo) {
              html += `<span class="tabla-secundario">Protocolo ${escaparHtml(protocolo)}</span>`;
            }

            // Regla de reenvío
            if (opts.usaReglaReenvio) {
              const cantReglas = opts.reglasReenvio?.length ?? 0;
              html += `<span class="tabla-secundario">${cantReglas} regla${cantReglas !== 1 ? 's' : ''} de reenvío</span>`;
            }

            // Objetivo AVL: con qué se identifica ante Suite911, la carta y hasta
            // cuándo transmite. Vigente va en rojo, como en el listado de
            // vehículos: es una emergencia en curso.
            if (opts.metodo === 'Objetivo AVL') {
              const avl = opts.opcionesObjetivoAVL;
              if (avl?.deviceid) {
                html += this.linea(
                  `<span class="tabla-codigo">${escaparHtml(avl.deviceid)}</span>`,
                  'fingerprint',
                );
              }
              if (avl?.nroCarta) {
                html += `<span class="tabla-secundario">Carta ${escaparHtml(avl.nroCarta)}</span>`;
              }
              if (configReenvioVigente(dato)) {
                const hasta = dato.periodoFin
                  ? ` hasta ${this.horaCorta(dato.periodoFin)}`
                  : '';
                html += `<span>${estadoHtml({ texto: `Transmitiendo${hasta}`, tono: 'error', icono: 'emergency_share' })}</span>`;
              } else if (dato.periodoFin) {
                html += this.linea(
                  `Terminó ${this.horaCorta(dato.periodoFin)}`,
                  'timer_off',
                );
              }
            }

            // Soflex
            if (opts.metodo === 'Soflex' && opts.opcionesSoflex) {
              if (opts.opcionesSoflex.providerid) {
                html += `<span class="tabla-secundario">Provider: ${escaparHtml(opts.opcionesSoflex.providerid)}</span>`;
              }
              if (opts.opcionesSoflex.version) {
                html += `<span class="tabla-secundario">v${escaparHtml(opts.opcionesSoflex.version)}</span>`;
              }
            }

            html += `</div>`;
            return html;
          },
          tooltip: (dato) => {
            const opts = dato.opcionesReenvio;
            if (!opts?.metodo) return undefined;
            const items: string[] = [];
            items.push(
              `<div><strong>Método:</strong> ${escaparHtml(opts.metodo)}</div>`,
            );
            if (opts.host)
              items.push(
                `<div><strong>Host:</strong> ${escaparHtml(opts.host)}</div>`,
              );
            if (opts.puerto)
              items.push(
                `<div><strong>Puerto:</strong> ${escaparHtml(opts.puerto)}</div>`,
              );
            const tooltipProtocolo =
              opts.metodo === 'Logictracker' ? 'http' : opts.protocolo;
            if (tooltipProtocolo)
              items.push(
                `<div><strong>Protocolo:</strong> ${escaparHtml(tooltipProtocolo)}</div>`,
              );
            if (opts.usaReglaReenvio)
              items.push(
                `<div><strong>Reglas:</strong> ${opts.reglasReenvio?.length ?? 0} configuradas</div>`,
              );
            return items.join('');
          },
          noData: 'Sin opciones de reenvío',
        },
      },
    ];

    // Cliente
    if (HelperService.getTipoCliente() !== 'Final') {
      columnas.push({
        header: {
          label: 'Cliente',
        },
        row: {
          html: (dato) =>
            dato.cliente?.nombre
              ? `<span class="tabla-principal">${escaparHtml(dato.cliente.nombre)}</span>`
              : '',
          tooltip: (dato) =>
            this.clienteJerarquia.tooltipJerarquia(dato.cliente),
          noData: 'Sin cliente',
        },
      });
    }

    // Acciones: no hay detalle; en el menú, Editar y Eliminar arriba y el
    // resto en el submenú Más acciones
    columnas.push({
      header: {
        label: 'Acciones',
      },
      row: {
        accionesGrouped: [
          {
            tipo: 'material',
            icon: 'edit',
            tooltip: 'Editar',
            click: (dato) => this.editar(dato),
            disabled: () =>
              !this.helper.tienePermiso(
                'Administración - Editar configuraciones de reenvíos',
              ),
          },
          {
            tipo: 'material',
            color: 'warn',
            icon: 'delete',
            tooltip: 'Eliminar',
            click: (dato) => this.eliminar(dato),
            disabled: () =>
              !this.helper.tienePermiso(
                'Administración - Eliminar configuraciones de reenvíos',
              ),
          },
          {
            tipo: 'material',
            icon: 'more_vert',
            tooltip: 'Más acciones',
            children: [
              {
                tipo: 'material',
                icon: 'rule',
                tooltip: 'Ver reglas de reenvío',
                click: (dato) => this.verReglasReenvio(dato),
                oculta: (dato) => !dato.opcionesReenvio?.usaReglaReenvio,
              },
              // Emergencia AVL por vehículo: el popup de extender / detener, el
              // mismo que se abre desde el listado de vehículos.
              {
                tipo: 'material',
                icon: 'emergency_share',
                tooltip: 'Ver / ajustar emergencia AVL',
                click: (dato) => this.ajustarEmergenciaAvl(dato),
                oculta: (dato) => !this.esEmergenciaAvl(dato),
                disabled: () =>
                  !this.helper.tienePermiso(
                    'Administración - Editar configuraciones de reenvíos',
                  ),
              },
              {
                tipo: 'material',
                icon: 'terminal',
                tooltip: 'Ver envíos a Suite911',
                click: (dato) => this.verEnviosAvl(dato),
                oculta: (dato) => !this.esEmergenciaAvl(dato),
                disabled: () =>
                  !this.helper.tienePermiso('Logs - Ver logs reenvios'),
              },
              {
                tipo: 'material',
                icon: 'science',
                tooltip: 'Probar configuración',
                click: (dato) => this.probarConfigSoflex(dato),
                oculta: (dato) => dato.opcionesReenvio?.metodo !== 'Soflex',
              },
              {
                tipo: 'material',
                icon: 'toggle_off',
                tooltip: 'Desactivar',
                click: (dato) => this.cambiarActivo(dato, false),
                oculta: (dato) => dato.activo === false,
                disabled: () =>
                  !this.helper.tienePermiso(
                    'Administración - Editar configuraciones de reenvíos',
                  ),
              },
              {
                tipo: 'material',
                icon: 'toggle_on',
                tooltip: 'Activar',
                click: (dato) => this.cambiarActivo(dato, true),
                oculta: (dato) => dato.activo !== false,
                disabled: () =>
                  !this.helper.tienePermiso(
                    'Administración - Editar configuraciones de reenvíos',
                  ),
              },
            ],
          },
        ],
      },
    });

    this.columnas = columnas;
  }

  // Hooks

  async ngOnInit() {
    // Soflex solo si el cliente tiene el módulo activo (igual que en crear/editar)
    if (LoginService.getCliente()?.config?.integracionSoflex?.activo) {
      this.filtroMetodo.elementos!.push('Soflex' as MetodoReenvio);
    }
    // Objetivo AVL, con el mismo criterio. El cliente se pide a la API y no a
    // la copia del login, que queda vieja hasta re-loguear (mismo motivo que
    // en el listado de vehículos). No se espera: el listado no depende de esto.
    void this.agregarMetodoAvlSiCorresponde();
    if (HelperService.getTipoCliente() !== 'Final') {
      this.filtros.push(this.cliente);
      this.filtros.push(this.clienteReenvio);
      this.filtros.push(this.ancestro);
    }
    this.setColumnas();
    await this.listar();
  }

  private async agregarMetodoAvlSiCorresponde(): Promise<void> {
    try {
      const cliente = await firstValueFrom(this.lista2.clientePropio());
      if (cliente?.config?.integracionObjetivosAVL?.activo) {
        // Array nuevo, no push: ng-select no ve la mutación después del
        // primer render.
        this.filtroMetodo.elementos = [
          ...(this.filtroMetodo.elementos ?? []),
          'Objetivo AVL' as MetodoReenvio,
        ];
      }
    } catch (error) {
      console.error('Error al resolver el módulo Objetivos AVL', error);
    }
  }

  ngOnDestroy() {
    this.datos$?.unsubscribe();
  }
}
